import {
  DatePickerInput,
  DropdownInputField,
  DropdownInputFieldProps,
} from "@surya-digital/leo-reactjs-material-ui";
import { Instance } from "mobx-state-tree";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { TimeOfDayModel } from "../../models/TimeOfDayModel";
import { observer } from "mobx-react";
import { ReadOnlyFieldContainer } from "./ReadOnlyFieldContainer";

interface EmailCycleTimeDropdownInputFieldProps
  extends Omit<
    DropdownInputFieldProps,
    "name" | "label" | "value" | "options" | "onSelect"
  > {
  date?: DatePickerInput;
  time: Instance<typeof TimeOfDayModel>;
  readOnly: boolean;
}

const TIME_SEPARATOR = ":";
const MERIDIEM = ["AM", "PM"] as const;
const INTERVALS = [0, 30];

const isSameDate = (date: Date, targetDate: Date): boolean =>
  date.toDateString() === targetDate.toDateString();

const getValue = (hour: number, minute: number): string =>
  [hour, minute].join(TIME_SEPARATOR);

const getEmailCycleTimeLabel = (hour: number, minute: number): string => {
  const hh = (hour % 12 || 12).toString().padStart(2, "0");
  const mm = minute.toString().padStart(2, "0");
  const meridiem = hour < 12 ? "AM" : "PM";

  return `${hh}${TIME_SEPARATOR}${mm} ${meridiem}`;
};

export const EmailCycleTimeDropdownInputField: React.FC<EmailCycleTimeDropdownInputFieldProps> =
  observer(({ date, time, readOnly, ...props }) => {
    const { t } = useTranslation();

    const options = React.useMemo<DropdownInputFieldProps["options"]>(() => {
      const times: DropdownInputFieldProps["options"] = [];
      const currentDate = new Date();

      MERIDIEM.forEach((meridiem) => {
        for (let index = 0; index < 12; index++) {
          const hour = ((index + 11) % 12) + 1;
          INTERVALS.forEach((minute) => {
            const hour24 = (hour % 12) + (meridiem === "PM" ? 12 : 0);

            if (date && isSameDate(date, currentDate)) {
              date.setHours(hour24, minute, 0, 0);
              if (date < currentDate) return;
            }

            times.push({
              name: getEmailCycleTimeLabel(hour24, minute),
              value: `${hour24}${TIME_SEPARATOR}${minute}`,
            });
          });
        }
      });

      return times;
    }, [date]);

    const selectedTimeValue = getValue(time.hour, time.minute);

    useEffect(() => {
      if (
        readOnly ||
        options.some(({ value }) => value === selectedTimeValue)
      ) {
        return;
      }

      const [firstOption] = options;
      if (!firstOption) return;

      const [hour, minute] = firstOption.value.split(TIME_SEPARATOR);
      time.setTimeOfDay(parseInt(hour, 10), parseInt(minute, 10));
    }, [options, selectedTimeValue, time]);

    const handleOnSelect: DropdownInputFieldProps["onSelect"] = ({ value }) => {
      const [hour, minute] = value.split(TIME_SEPARATOR);
      time.setTimeOfDay(parseInt(hour, 10), parseInt(minute, 10));
    };

    if (readOnly) {
      return (
        <ReadOnlyFieldContainer label={t("emailCycles.time")} required>
          {getEmailCycleTimeLabel(time.hour, time.minute)}
        </ReadOnlyFieldContainer>
      );
    }

    return (
      <DropdownInputField
        name="emailCycleTimeDropdown"
        label={t("emailCycles.time")}
        value={selectedTimeValue}
        options={options}
        onSelect={handleOnSelect}
        {...props}
      />
    );
  });
